import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClerk } from '@clerk/clerk-react';

import { useSignOut } from '@/auth/useSignOut';
import { SUPPORTED_LANGUAGES, setLanguage } from '@/i18n';

import Sheet from './Sheet';

/** Points per event, from the contest, so the rules print whatever Admin has set. */
export type ScoringPoints = { goal: number; shot: number; tackle: number; interception: number };

export default function InfoButton({ points }: { points: ScoringPoints }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const signOut = useSignOut();
  const { openUserProfile } = useClerk();
  const rules = t('info.rules', { returnObjects: true, ...points }) as string[];

  return (
    <>
      <button
        type="button"
        aria-label={t('info.howToPlay')}
        onClick={() => setOpen(true)}
        className="w-[40px] h-[40px] flex items-center justify-center transition-transform duration-150 active:scale-90"
      >
        <img src="/info.svg" alt="" className="w-[22px] h-[22px]" />
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} title={t('info.title')}>
        <div className="flex min-h-full flex-col">
          <ol className="px-[16px] pt-[16px] flex flex-col gap-[16px]">
            {rules.map((rule, index) => (
              <li key={rule} className="flex items-start gap-[12px]">
                <span
                  aria-hidden
                  className="shrink-0 w-[24px] h-[24px] rounded-full flex items-center justify-center font-poppins font-semibold text-[12px] leading-none text-white"
                  style={{ background: '#E02125' }}
                >
                  {index + 1}
                </span>
                {/* Nudged down onto the badge's optical centre line. */}
                <span className="pt-[3px] font-poppins font-medium text-[12px] leading-[17px] text-white">
                  {rule}
                </span>
              </li>
            ))}
          </ol>
          <div className="px-[16px] pt-[24px] flex items-center gap-[8px]">
            <span className="font-poppins text-[12px] text-white/60">{t('info.language')}</span>
            {SUPPORTED_LANGUAGES.map(lng => {
              const active = (i18n.resolvedLanguage ?? 'en') === lng;
              return (
                <button
                  key={lng}
                  type="button"
                  onClick={() => setLanguage(lng)}
                  aria-pressed={active}
                  className={`h-8 min-w-[44px] rounded-full px-3 font-poppins text-[12px] font-semibold uppercase transition-all duration-150 active:scale-[0.98] ${
                    active ? 'bg-white text-black' : 'border border-white/20 text-white'
                  }`}
                >
                  {lng}
                </button>
              );
            })}
          </div>

          <div className="px-[16px] pt-[24px]">
            <button
              type="button"
              onClick={() => setRulesOpen(true)}
              className="w-full h-12 rounded-full bg-white font-poppins text-[14px] font-semibold text-black transition-all duration-150 active:scale-[0.98]"
            >
              {t('info.officialRules')}
            </button>
          </div>

          <div className="mt-auto px-[16px] pt-[32px]">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                openUserProfile();
              }}
              className="mb-[8px] w-full h-12 rounded-full border border-white/20 bg-transparent font-poppins text-[14px] font-semibold text-white transition-all duration-150 active:scale-[0.98]"
            >
              {t('info.account')}
            </button>
            <button
              type="button"
              onClick={() => void signOut()}
              className="w-full h-12 rounded-full border border-white/20 bg-transparent font-poppins text-[14px] font-semibold text-white transition-all duration-150 active:scale-[0.98]"
            >
              {t('info.logOut')}
            </button>
          </div>
        </div>
      </Sheet>
      <Sheet open={rulesOpen} onClose={() => setRulesOpen(false)} title={t('info.officialRules')}>
        <iframe
          src={`/rules/${i18n.resolvedLanguage ?? 'en'}.html`}
          title={t('info.officialRules')}
          className="block h-full w-full border-0"
        />
      </Sheet>
    </>
  );
}
